import { useNavigate } from "@tanstack/react-router";
import { CircleHelp, Plus, Smartphone, Sparkles, X } from "lucide-react";
import * as React from "react";
import { toast } from "sonner";

import { AppPicker } from "@/components/kb/app-mark";
import { LeagueBadge } from "@/components/kb/league-badge";
import { PushPreview } from "@/components/kb/notification-card";
import {
	ComparisonField,
	RuleSentence,
	StatField,
	SubjectField,
	WindowField,
} from "@/components/kb/rule-sentence";
import { formatDateTime } from "@/components/kb/status-pill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useEntityMaps } from "@/hooks/useEntityMaps";
import { useCreateRule, useParseRule, useUpdateRule } from "@/hooks/useRules";
import {
	Condition,
	type CooldownPreset,
	type Duration,
	type ParsedRuleResponse,
	type RuleDTO,
} from "@/lib/api";
import { demoGame } from "@/lib/demo";
import {
	buildEntityOptions,
	type EntityOption,
	findEntityOption,
	type Players,
	type Teams,
} from "@/lib/entities";
import { featuredApps } from "@/lib/featured-apps";
import { type ParsedRule, parseRule } from "@/lib/parse-rule";
import {
	conditionToPrompt,
	flattenLeaves,
	hasValue,
	teamAbbr,
} from "@/lib/rule-display";

const EXAMPLES = [
	"When Aaron Judge hits a home run, notify me.",
	"When the Cardinals get at least 5 RBI in a game, notify me.",
	"When Shohei Ohtani has an on-base percentage of at least .400 in a game, notify me.",
];

type CooldownOption = {
	key: CooldownPreset | "keep";
	label: string;
	unit: Duration;
	value: number;
};

const COOLDOWNS: CooldownOption[] = [
	{ key: "none", label: "No quiet period", unit: "minutes", value: 0 },
	{ key: "15m", label: "15 minutes", unit: "minutes", value: 15 },
	{ key: "1h", label: "1 hour", unit: "hours", value: 1 },
	{ key: "6h", label: "6 hours", unit: "hours", value: 6 },
	{ key: "24h", label: "24 hours", unit: "hours", value: 24 },
	{
		key: "once-per-game",
		label: "Once per game",
		unit: "minutes",
		value: 0,
	},
	{ key: "once", label: "Only once, then pause", unit: "minutes", value: 0 },
];

function blankCondition(): Condition {
	const condition = Condition.makeDefault();
	condition.operator = "gte";
	condition.stat = null;
	condition.value = undefined;

	return condition;
}

function missingFields(condition: Condition): string[] {
	const missing = new Set<string>();

	for (const leaf of flattenLeaves(condition)) {
		if (!leaf.entityID || !leaf.entityType) {
			missing.add("who to watch");
		}

		if (!leaf.stat) {
			missing.add("which stat");
		}

		if (!hasValue(leaf)) {
			missing.add("the number to hit");
		}
	}

	return [...missing];
}

export function RuleEditor({ rule }: { rule?: RuleDTO }) {
	const entities = useEntityMaps();

	if (entities.isLoading) {
		return (
			<div className="flex flex-col gap-6">
				<Skeleton className="h-10 w-2/3" />
				<Skeleton className="h-40 w-full rounded-xl" />
				<Skeleton className="h-96 w-full rounded-xl" />
			</div>
		);
	}

	return (
		<RuleEditorForm
			players={entities.players}
			rule={rule}
			teams={entities.teams}
		/>
	);
}

function RuleEditorForm({
	rule,
	players,
	teams,
}: {
	rule?: RuleDTO;
	players: Players;
	teams: Teams;
}) {
	const navigate = useNavigate();
	const createRule = useCreateRule();
	const updateRule = useUpdateRule();
	const parseRemote = useParseRule();
	const [condition, setCondition] = React.useState(() =>
		rule ? Condition.fromAny(rule.rule) : blankCondition(),
	);
	const [prompt, setPrompt] = React.useState(() =>
		rule ? rule.prompt || conditionToPrompt(condition, players, teams) : "",
	);
	const [apps, setApps] = React.useState<string[]>(rule?.apps ?? []);
	const [ambiguous, setAmbiguous] = React.useState<ParsedRule["ambiguous"]>();
	const quietUntil = rule?.notifyAgainAfter
		? new Date(rule.notifyAgainAfter).getTime()
		: 0;
	const remainingQuietMs = quietUntil - Date.now();
	const hasQuietPeriod = remainingQuietMs > 0;
	const cooldownOptions = React.useMemo<CooldownOption[]>(() => {
		if (!hasQuietPeriod || !rule?.notifyAgainAfter) {
			return COOLDOWNS;
		}

		return [
			{
				key: "keep",
				label: `Keep quiet until ${formatDateTime(rule.notifyAgainAfter)}`,
				unit: "minutes" as Duration,
				value: remainingQuietMs / 60_000,
			},
			...COOLDOWNS,
		];
	}, [hasQuietPeriod, remainingQuietMs, rule?.notifyAgainAfter]);
	const [cooldownKey, setCooldownKey] = React.useState<CooldownOption["key"]>(
		hasQuietPeriod ? "keep" : (rule?.cooldown ?? "15m"),
	);
	const [, rerender] = React.useReducer((value: number) => value + 1, 0);

	const options = React.useMemo(
		() => buildEntityOptions(players, teams),
		[players, teams],
	);

	const parseTimer = React.useRef<number | undefined>(undefined);
	const parseSequence = React.useRef(0);

	React.useEffect(
		() => () => {
			if (parseTimer.current) {
				window.clearTimeout(parseTimer.current);
			}
		},
		[],
	);

	const leaves = flattenLeaves(condition);
	const missing = missingFields(condition);
	const pending = createRule.isPending || updateRule.isPending;
	const saving = rule ? "Save changes" : "Save rule";

	const adaptAmbiguous = (
		parsed: ParsedRuleResponse["ambiguous"],
	): ParsedRule["ambiguous"] => {
		if (!parsed) {
			return undefined;
		}

		return {
			phrase: parsed.phrase,
			options: parsed.options.map(
				(option) =>
					findEntityOption(options, option.entityType, option.entityID) ?? {
						entityID: option.entityID,
						entityType: option.entityType,
						group: option.entityType === "team" ? "Teams" : "Players",
						label: option.label,
						meta: "MLB",
						search: option.label.toLowerCase(),
						value: `${option.entityType}:${option.entityID}`,
					},
			),
		};
	};

	const runParse = (text: string) => {
		if (!text.trim()) {
			parseSequence.current++;
			setCondition(blankCondition());
			setAmbiguous(undefined);
			setApps([]);
			return;
		}

		const parsed = parseRule(text, options);
		setCondition(parsed.condition);
		setAmbiguous(parsed.ambiguous);

		const sequence = ++parseSequence.current;

		parseRemote.mutate(text, {
			onSuccess: (remote) => {
				if (sequence !== parseSequence.current) {
					return;
				}

				setCondition(Condition.fromAny(remote.condition));
				setAmbiguous(adaptAmbiguous(remote.ambiguous));
				setApps(remote.apps);
				setCooldownKey(remote.cooldown);
			},
		});
	};

	const handlePromptChange = (next: string) => {
		setPrompt(next);

		if (parseTimer.current) {
			window.clearTimeout(parseTimer.current);
		}

		parseTimer.current = window.setTimeout(() => {
			runParse(next);
		}, 250);
	};

	const applyExample = (example: string) => {
		if (parseTimer.current) {
			window.clearTimeout(parseTimer.current);
		}

		setPrompt(example);
		runParse(example);
	};

	const clearPrompt = () => {
		if (parseTimer.current) {
			window.clearTimeout(parseTimer.current);
		}

		parseSequence.current++;
		setPrompt("");
		setApps([]);
		setAmbiguous(undefined);
		setCondition(blankCondition());
	};

	const chooseAmbiguous = (option: EntityOption) => {
		for (const leaf of leaves) {
			if (!leaf.entityID) {
				leaf.entity = option.value;
			}
		}

		setAmbiguous(undefined);
		rerender();
	};

	const save = () => {
		const problems = new Set<string>();

		for (const leaf of leaves) {
			if (!leaf.entityID || !leaf.entityType) {
				problems.add("who to watch");
			}

			if (!leaf.stat) {
				problems.add("which stat");
			}

			if (!hasValue(leaf)) {
				problems.add("the number to hit");
			}

			leaf.over = leaf.over ?? 1;
			leaf.window = leaf.window ?? "game";
		}

		if (leaves.length === 0 || problems.size > 0) {
			toast.error("Finish the rule first", {
				description: `Still need ${[...problems].join(", ") || "a condition"}.`,
			});
			return;
		}

		const cooldown =
			cooldownOptions.find((option) => option.key === cooldownKey) ??
			COOLDOWNS[0];
		const cooldownValue: CooldownPreset =
			cooldown.key === "keep" ? (rule?.cooldown ?? "none") : cooldown.key;
		const notifyAgainAfter = {
			unit: cooldown.unit,
			value: cooldown.value,
		};
		const payload = {
			apps,
			condition: condition.toAny(),
			cooldown: cooldownValue,
			enabled: rule?.enabled ?? true,
			notifyAgainAfter,
			prompt,
		};
		const onError = (error: Error) =>
			toast.error(rule ? "Could not update rule" : "Could not create rule", {
				description: error.message,
			});
		const onSuccess = () => {
			toast.success(rule ? "Rule updated" : "Rule created");
			void navigate({ to: "/rules" });
		};

		if (rule) {
			updateRule.mutate({ ...payload, id: rule.id }, { onError, onSuccess });

			return;
		}

		createRule.mutate(payload, { onError, onSuccess });
	};

	return (
		<div className="flex flex-col gap-8" data-testid="rule-editor">
			<div className="flex flex-col gap-2">
				<p className="type-eyebrow text-muted-foreground">
					{rule ? "Edit rule" : "New rule"}
				</p>
				<h1 className="type-display" data-testid="page-title">
					What should we watch for?
				</h1>
				<p
					className="max-w-2xl text-muted-foreground"
					data-testid="page-description"
				>
					Say it like you would tell a friend. We turn it into a rule you can
					check and correct before saving.
				</p>
			</div>

			<div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_19rem]">
				<div className="flex min-w-0 flex-col gap-5">
					<div className="rounded-xl border bg-card p-4 shadow-xs sm:p-5">
						<label className="sr-only" htmlFor="rule-prompt">
							Describe your rule
						</label>
						<textarea
							className="min-h-28 w-full resize-none bg-transparent text-lg leading-relaxed outline-none placeholder:text-muted-foreground/70"
							data-testid="rule-prompt"
							id="rule-prompt"
							onChange={(event) => handlePromptChange(event.target.value)}
							placeholder="When Aaron Judge hits a home run, notify me."
							value={prompt}
						/>
						<div className="flex items-center justify-between gap-3 border-t pt-3">
							<span className="flex items-center gap-1.5 text-xs text-muted-foreground">
								<Sparkles className="size-3.5" />
								We read it as you type
							</span>
							{prompt.length > 0 && (
								<Button onClick={clearPrompt} size="xs" variant="ghost">
									<X />
									Clear
								</Button>
							)}
						</div>
					</div>

					<div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 md:mx-0 md:flex-wrap md:px-0">
						{EXAMPLES.map((example) => (
							<button
								className="shrink-0 rounded-full border bg-card px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
								key={example}
								onClick={() => applyExample(example)}
								type="button"
							>
								{example}
							</button>
						))}
					</div>

					<div className="overflow-hidden rounded-xl border bg-card shadow-xs">
						<div className="flex flex-col gap-5 p-4 sm:p-5">
							<div className="flex flex-wrap items-center justify-between gap-3">
								<span className="type-eyebrow">
									Here&rsquo;s what we understood
								</span>
								<LeagueBadge league="MLB" />
							</div>

							<div className="rounded-lg bg-muted/50 p-4">
								<RuleSentence
									condition={condition}
									editable
									onChange={rerender}
									options={options}
									players={players}
									size="lg"
									teams={teams}
									testId="rule-sentence"
								/>
							</div>
							<p className="-mt-2 text-xs text-muted-foreground">
								Tap any highlighted word to change it.
							</p>

							{ambiguous && (
								<div className="rounded-lg bg-signal-soft p-3 text-sm">
									<div className="flex items-start gap-2">
										<CircleHelp className="mt-0.5 size-4 shrink-0 text-signal-ink" />
										<div className="flex flex-col gap-2">
											<p className="font-medium text-signal-ink">
												Which {ambiguous.phrase} did you mean?
											</p>
											<div className="flex flex-wrap gap-1.5">
												{ambiguous.options.slice(0, 8).map((option) => (
													<button
														className="rounded-full border border-signal-ink/25 bg-background/60 px-2.5 py-1 text-xs font-medium hover:bg-background"
														key={option.value}
														onClick={() => chooseAmbiguous(option)}
														type="button"
													>
														{option.label}
													</button>
												))}
											</div>
										</div>
									</div>
								</div>
							)}

							{missing.length > 0 && (
								<div
									className="flex items-start gap-2.5 rounded-lg border border-dashed p-3 text-sm text-muted-foreground"
									data-testid="missing-fields"
								>
									<CircleHelp className="mt-0.5 size-4 shrink-0" />
									<div>
										<p className="font-medium text-foreground">
											Still need {missing.join(", ")}.
										</p>
										<p>
											Use a highlighted word in the sentence or the fields
											below.
										</p>
									</div>
								</div>
							)}

							<Separator />

							<div className="flex flex-col gap-3">
								<div className="flex flex-wrap items-center justify-between gap-2">
									<span className="type-eyebrow">Conditions</span>
									{condition.ruleType !== "leaf" && leaves.length > 1 && (
										<ToggleGroup
											data-testid="condition-combiner"
											onValueChange={(value) => {
												if (value === "and" || value === "or") {
													condition.ruleType = value;
													rerender();
												}
											}}
											type="single"
											value={condition.ruleType}
										>
											<ToggleGroupItem value="and">All</ToggleGroupItem>
											<ToggleGroupItem value="or">Any</ToggleGroupItem>
										</ToggleGroup>
									)}
								</div>

								{leaves.map((leaf, index) => (
									<div
										className="flex flex-col gap-3 rounded-lg border p-3"
										data-testid={`condition-${index}`}
										key={leaf.id}
									>
										<div className="flex items-center justify-between gap-2">
											<span className="type-eyebrow text-muted-foreground">
												Condition {index + 1}
											</span>
											{leaves.length > 1 && (
												<Button
													aria-label="Remove condition"
													data-testid={`remove-condition-${index}`}
													onClick={() => {
														condition.remove(leaf.id);
														rerender();
													}}
													size="icon-xs"
													variant="ghost"
												>
													<X />
												</Button>
											)}
										</div>

										<div className="grid gap-3 sm:grid-cols-2">
											<div className="sm:col-span-2">
												<SubjectField
													onChange={(option) => {
														leaf.entity = option.value;
														rerender();
													}}
													options={options}
													testId={`subject-${index}`}
													value={leaf.entity}
												/>
											</div>
											<StatField
												onChange={(stat) => {
													leaf.stat = stat;
													rerender();
												}}
												testId={`stat-${index}`}
												value={leaf.stat}
											/>
											<ComparisonField
												onChange={(operator) => {
													leaf.operator = operator;
													rerender();
												}}
												testId={`comparison-${index}`}
												value={leaf.operator}
											/>
											<div className="flex flex-col gap-1.5">
												<span className="text-xs text-muted-foreground">
													Value
												</span>
												<Input
													className="h-9"
													data-testid={`value-${index}`}
													onChange={(event) => {
														const next = event.target.value;
														leaf.value = next === "" ? undefined : Number(next);
														rerender();
													}}
													step="any"
													type="number"
													value={leaf.value ?? ""}
												/>
											</div>
											<WindowField
												onChange={(window) => {
													leaf.window = window;
													leaf.over = leaf.over ?? 1;
													rerender();
												}}
												onOverChange={(over) => {
													leaf.over = over;
													rerender();
												}}
												over={leaf.over}
												testId={`window-${index}`}
												value={leaf.window}
											/>
										</div>
									</div>
								))}

								<Button
									className="self-start"
									data-testid="add-condition"
									onClick={() => {
										condition.addDefault();
										rerender();
									}}
									size="sm"
									variant="ghost"
								>
									<Plus />
									Add condition
								</Button>
							</div>

							<Separator />

							<div className="flex flex-col gap-3">
								<div className="flex flex-wrap items-center justify-between gap-2">
									<span className="type-eyebrow">Open from the alert</span>
									<span className="text-xs text-muted-foreground">
										{apps.length === 0
											? "Push notification"
											: `${apps.length} app${apps.length === 1 ? "" : "s"}`}
									</span>
								</div>
								<AppPicker
									apps={featuredApps}
									onChange={setApps}
									value={apps}
								/>
								<div className="flex items-start gap-2.5 rounded-lg bg-muted/60 p-3 text-sm text-muted-foreground">
									<Smartphone className="mt-0.5 size-4 shrink-0" />
									<p>
										Pick the app to open when this rule fires. Choose none and
										the alert stays a push notification.
									</p>
								</div>
								{apps.length === 0 && /\b(?:open|launch)\b/i.test(prompt) && (
									<p className="text-xs text-signal-ink">
										We couldn&rsquo;t match an app in that prompt yet. Pick one
										above or rephrase it.
									</p>
								)}
							</div>

							<Separator />

							<div className="flex flex-col gap-2">
								<label className="type-eyebrow" htmlFor="rule-cooldown">
									Notify again
								</label>
								<select
									className="h-9 w-full rounded-md border bg-card px-3 text-sm outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
									data-testid="cooldown-select"
									id="rule-cooldown"
									onChange={(event) =>
										setCooldownKey(event.target.value as CooldownOption["key"])
									}
									value={cooldownKey}
								>
									{cooldownOptions.map((cooldown) => (
										<option key={cooldown.key} value={cooldown.key}>
											{cooldown.label}
										</option>
									))}
								</select>
								<p className="text-xs text-muted-foreground">
									Quiet periods start when you save. Keep the current quiet
									period to preserve when alerts can resume.
								</p>
							</div>
						</div>

						<div className="flex flex-wrap items-center gap-2 border-t bg-muted/50 p-4 sm:px-5">
							<Button data-testid="save-rule" disabled={pending} onClick={save}>
								{saving}
							</Button>
							<Button
								data-testid="cancel-rule"
								onClick={() => void navigate({ to: "/rules" })}
								variant="ghost"
							>
								Cancel
							</Button>
						</div>
					</div>
				</div>

				<aside className="flex flex-col gap-3 lg:sticky lg:top-10 lg:h-fit">
					<p className="type-eyebrow text-muted-foreground">On your phone</p>
					<div className="relative overflow-hidden rounded-[2rem] border-[6px] border-board bg-board p-3 pt-10 pb-16 shadow-xl">
						<div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_0%,color-mix(in_oklch,var(--signal)_18%,transparent),transparent_55%),radial-gradient(circle_at_90%_100%,color-mix(in_oklch,var(--nfl)_22%,transparent),transparent_55%)]" />
						<div className="relative flex flex-col items-center text-device-foreground">
							<span className="type-stat text-4xl">1:58</span>
							<span className="text-xs opacity-60">Sunday, November 1</span>
							{(() => {
								const game = demoGame(rule?.id ?? "new-rule");

								return (
									<PushPreview
										apps={apps}
										className="mt-6 w-full text-left"
										detail={`${teamAbbr(game.awayTeam)} ${game.awayScore ?? "–"} – ${game.homeScore ?? "–"} ${teamAbbr(game.homeTeam)} · ${game.clock ?? "Live"}`}
										headline={conditionToPrompt(
											condition,
											players,
											teams,
										).replace(/^When /, "")}
									/>
								);
							})()}
						</div>
					</div>
					<p className="text-xs text-muted-foreground">
						Game details come from the live feed when the rule fires. The
						preview uses a sample game.
					</p>
				</aside>
			</div>
		</div>
	);
}
